import * as SignalR from '@microsoft/signalr';
import { IAppContext } from 'App/AppContext';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';

export interface IHubMethod {
  method: string;
  callback: unknown;
}

export interface IHubConnection {
  readonly connectionId: string | null;
  connection: SignalR.HubConnection;
}

const SendConnectionId = 'SendConnectionId';

// Reconnect at 0s, 2s, 5s, 10s, 30s, then once every 60s up to ~5 min total.
const reconnectSchedule = [0, 2000, 5000, 10000, 30000, 60000, 60000, 60000];

const getServerConnectionId = async (connection: SignalR.HubConnection): Promise<string | null> => {
  try {
    return await connection.invoke<string>(SendConnectionId);
  } catch (err) {
    logger.debug('Error getting hub connection id', err);

    return null;
  }
};

export const startHubConnection = async (
  appContext: IAppContext,
  hubUrl: string,
  methods: IHubMethod[],
): Promise<IHubConnection | null> => {
  try {
    // The hub URL is tenant-scoped. user.login.tenantId is the tenant the user
    // operates in (org-unit id when in a sub-org-unit, top-level tenant
    // otherwise) and is always set post-login -- unlike globalOrgUnitId, which
    // is undefined for top-level-tenant users. Callers with a restart loop
    // (TaskViewHubProvider) gate their connect effect on this same value.
    const tenantId = appContext.user?.login?.tenantId;
    if (!tenantId) {
      logger.debug('startHubConnection: user tenantId not available, skipping connect');

      return null;
    }
    const baseUrl = Config.get('Api.BaseURL');
    const url = `${baseUrl}/${hubUrl}?orgunitid=${tenantId}`;
    const newConnection = new SignalR.HubConnectionBuilder()
      .configureLogging(SignalR.LogLevel.Error)
      .withUrl(url, {
        skipNegotiation: true,
        transport: SignalR.HttpTransportType.WebSockets,
        // Pass the function reference so MSAL acquireTokenSilent runs on every
        // retry; capturing the resolved value here makes the factory hand out
        // the same token forever and reconnects 401 once it expires.
        accessTokenFactory: () => appContext.getAccessToken(apiRequest.scopes),
      })
      .withAutomaticReconnect(reconnectSchedule)
      .build();

    let serverConnectionId: string | null = null;

    if (newConnection) {
      for (let i = 0; i < methods.length; i++) {
        const { method, callback } = methods[i];
        newConnection.on(method, callback as never);
      }
      await newConnection.start();
      serverConnectionId = newConnection.connectionId ?? (await getServerConnectionId(newConnection));
      newConnection.onreconnected(async (connectionId) => {
        serverConnectionId = connectionId ?? newConnection.connectionId ?? (await getServerConnectionId(newConnection));
      });
    }

    return {
      // Live getter -- SignalR assigns a new id on each reconnect; consumers
      // reading a cached value would silently get a stale one.
      get connectionId() {
        return newConnection?.connectionId ?? serverConnectionId;
      },
      connection: newConnection,
    };
  } catch (err) {
    // allowed to fail
    // e.g. proxy blocking websockets
    // clients should handle null connection and disable streaming updates
    logger.debug('Error starting hub connection', err);

    return null;
  }
};

export const stopHubConnection = async (
  hubConnection: SignalR.HubConnection | IHubConnection | null | undefined,
): Promise<void> => {
  try {
    if (hubConnection) {
      if (!(hubConnection instanceof SignalR.HubConnection)) {
        hubConnection = hubConnection.connection;
      }
      if (hubConnection.state === SignalR.HubConnectionState.Connected) {
        await hubConnection.stop();
      }
    }
  } catch {
    //swallow error
  }
};
